import { Box, Button, Icon, IconButton, Link, Stack, Typography } from '@mui/material';
import { ApiError, ApplicationCustomField } from '@schooly/api';

import {
  ApplicationAdditionalInformationPreview,
  ApplicationPreviewInfoRow,
  ApplicationUpdateForm,
  CreateParentForm,
  CustomFieldFormContainer,
  EditDialog,
  ErrorEmailBlock,
  getCustomFieldsToShow,
  ParentInfoFormContainer,
  PreviewFormType,
  RowsContainer,
  useSchoolEmailInUseValidation,
} from '@schooly/components/applications';
import { useAuth } from '@schooly/components/authentication';
import { DuplicatesRow } from '@schooly/components/duplicates';
import { ControlTextField } from '@schooly/components/form-text-field';
import { useNotifications } from '@schooly/components/notifications';
import {
  AREA_CODE_OPTIONS,
  GENDER_OPTIONS,
  Genders,
  getLabelIdForOption,
  LANGUAGE_OPTIONS,
  Languages,
  NATIONALITY_OPTIONS,
  RELATIONSHIP_TYPE_OPTIONS,
  RelationsToChild,
  USER_NATIONALITIES_MAX_COUNT,
  VALID_EMAIL_REGEXP,
} from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  EditIcon,
  EmailIcon,
  FemaleIcon,
  FlagIcon,
  Language3Icon,
  MaleIcon,
  PhoneIcon,
  ProfessionIcon,
  Bubble,
} from '@schooly/style';
import {
  convertEmailInUseRelationToParentForm,
  convertParentFormToApplicationParent,
  emailIsDuplicatedInApplication,
  getEmailInUseForSchoolErrorTextId,
} from '@schooly/utils/application-helpers';
import { getNationalitiesLabelIds } from '@schooly/utils/get-nationalities-label-ids';
import { getUserFullNameWithTitle } from '@schooly/utils/get-user-full-name-with-title';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import FormSelect2 from '../../../components/ui/Input/FormSelect2';
import { FormRadioGroup } from '../../../components/uikit-components/FormCheckbox/FormRadioGroup';
import { GridRowName } from '../../../components/uikit-components/Grid/Grid';
import { LongNameWithVerticalTooltip } from '../../../components/uikit-components/LongNameWithVerticalTooltip/LongNameWithVerticalTooltip';

export interface ApplicationEditablePreviewParentProps {
  parent: CreateParentForm;
  onSubmit: (v: CreateParentForm, type: string) => void;
  canEdit: boolean;
  updating?: Record<string, boolean>;
  updatingIcon?: React.ReactNode;
  readonlyIcon?: React.ReactNode;
  withoutActionBackground?: boolean;
  onAddExistingAdult?: (adult: CreateParentForm) => void;
  formData: ApplicationUpdateForm;
}

export const ApplicationEditablePreviewParent: FC<ApplicationEditablePreviewParentProps> = ({
  parent,
  onSubmit,
  canEdit,
  updating,
  updatingIcon,
  readonlyIcon,
  withoutActionBackground,
  onAddExistingAdult,
  formData,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { showError } = useNotifications();
  const [isFormOpen, showFormModal, hideFormModal] = useFlag();

  const [previewType, setPreviewType] = useState<PreviewFormType | null>(null);
  const [selectedCustomField, setSelectedCustomField] = useState<ApplicationCustomField | null>(
    null,
  );

  const { isValidating, checkUserEmailInUse, emailOwner, emailOwnerType } =
    useSchoolEmailInUseValidation();
  const emailInUseErrorTextId = getEmailInUseForSchoolErrorTextId(!!emailOwner);

  useEffect(() => {
    if (previewType && !isFormOpen) {
      setPreviewType(null);
    }
  }, [previewType, isFormOpen]);

  const form = useForm<CreateParentForm>({
    values: parent,
    mode: 'onChange',
  });

  const canEditPersonalData = canEdit && !!parent.can_be_edited;
  const canEditCustomFields = canEdit;

  const onEditClick = (editType: PreviewFormType, customField?: ApplicationCustomField) => {
    setPreviewType(editType);
    setSelectedCustomField(customField ?? null);
    showFormModal();
  };

  const nationalities = form.watch('nationalities') ?? [];
  const hasEmailInUseError = form.formState.errors.email?.type === 'email-in-use';

  const handleAddExistingAdult = useCallback(() => {
    if (!emailOwner || !onAddExistingAdult) return;
    const parent = convertEmailInUseRelationToParentForm(emailOwner, []);
    onAddExistingAdult(parent);
  }, [emailOwner, onAddExistingAdult]);

  const validateEmail = useCallback(
    (email?: string) => {
      if (!email) return true;

      const isDuplicated = emailIsDuplicatedInApplication({
        email,
        parentId: parent.id,
        allParents: formData.parents,
      });

      if (isDuplicated) {
        return formatMessage({ id: 'applications-ErrorEmail-EmailUsedInApplication' });
      }

      return true;
    },
    [parent.id, formData.parents, formatMessage],
  );

  const formContent = useMemo(() => {
    switch (previewType) {
      case 'telephone':
        return (
          <Stack direction="row" gap={1.25}>
            <Box flex="0 0 250px">
              <FormSelect2
                name="phoneCode"
                labelTextId="AreaCode"
                options={AREA_CODE_OPTIONS}
                noRequiredLabel
                showSelectedValue
                optionClassName="CountryCodeSelectOption"
              />
            </Box>

            <ControlTextField
              name="telephone"
              control={form.control}
              label={formatMessage({ id: 'peopleDetail-PhoneNumber' })}
              canClear
              fullWidth
            />
          </Stack>
        );
      case 'email': {
        return (
          <Stack gap={1}>
            <ControlTextField
              name="email"
              control={form.control}
              label={formatMessage({ id: 'peopleDetail-EmailAddressPersonal' })}
              canClear={!isValidating}
              fullWidth
              //TODO: Refactor email validation - TR-4353
              rules={{
                required: true,
                pattern: {
                  value: VALID_EMAIL_REGEXP,
                  message: formatMessage({
                    id: 'input-ErrorInvalidEmail',
                  }),
                },
                validate: validateEmail,
              }}
            />
            {hasEmailInUseError && (
              <ErrorEmailBlock textId={emailInUseErrorTextId}>
                {emailOwner && emailOwnerType && (
                  <DuplicatesRow
                    duplicate={{ ...emailOwner, avatar: emailOwner.avatar ?? undefined }}
                    userType={emailOwnerType}
                    endAction={
                      <Button onClick={handleAddExistingAdult}>
                        {formatMessage({ id: 'applications-AddExistingParent' })}
                      </Button>
                    }
                  />
                )}
              </ErrorEmailBlock>
            )}
          </Stack>
        );
      }
      case 'language': {
        return (
          <FormSelect2
            name="language"
            labelTextId="peopleDetail-PrimaryLanguage"
            options={LANGUAGE_OPTIONS}
          />
        );
      }
      case 'gender': {
        return (
          <FormRadioGroup
            options={[...GENDER_OPTIONS].reverse()}
            name="gender"
            rules={{ required: true }}
          />
        );
      }
      case 'nationalities': {
        return (
          <FormSelect2
            name="nationalities"
            labelTextId={
              nationalities.length <= 1
                ? 'peopleDetail-Nationality'
                : 'peopleDetail-Nationality-plural'
            }
            options={NATIONALITY_OPTIONS}
            rules={{ required: true }}
            multiple
            maxCount={USER_NATIONALITIES_MAX_COUNT}
          />
        );
      }
      case 'profession': {
        return (
          <ControlTextField
            name="profession"
            control={form.control}
            label={formatMessage({ id: 'peopleDetail-Profession' })}
            canClear
            fullWidth
          />
        );
      }
      case 'userInfo': {
        return <ParentInfoFormContainer />;
      }

      case 'relationshipType': {
        return (
          <FormSelect2
            name="relationship_type"
            labelTextId="relationship"
            options={RELATIONSHIP_TYPE_OPTIONS}
          />
        );
      }

      default:
        return null;
    }
  }, [
    previewType,
    form.control,
    formatMessage,
    isValidating,
    validateEmail,
    hasEmailInUseError,
    emailInUseErrorTextId,
    emailOwner,
    emailOwnerType,
    handleAddExistingAdult,
    nationalities.length,
  ]);

  useEffect(() => {
    if (!form.formState.isValid) {
      form.trigger();
    }
  }, [form]);

  const nationalitiesString = useMemo(() => {
    if (!parent.nationalities?.length) return '';
    return getNationalitiesLabelIds(parent.nationalities)
      .map((id) => formatMessage({ id }))
      .join(', ');
  }, [formatMessage, parent.nationalities]);

  const normalizedParent = convertParentFormToApplicationParent(parent);

  const customFieldsToShow = useMemo(
    () => getCustomFieldsToShow(normalizedParent.custom_fields ?? []),
    [normalizedParent.custom_fields],
  );

  const parentHasCustomFields = !!customFieldsToShow.length;

  const handleSubmit = useCallback(
    async (data: CreateParentForm, type: string) => {
      if (type !== 'email') {
        onSubmit(data, type);
        hideFormModal();
        return;
      }

      if (!data.email) return;

      try {
        const { email_in_use } = await checkUserEmailInUse({
          email: data.email,
          schoolId,
        });

        if (email_in_use) {
          form.setError('email', { type: 'email-in-use' });
          return;
        }

        onSubmit(data, type);
        hideFormModal();
      } catch (error) {
        showError(error as ApiError);
      }
    },
    [checkUserEmailInUse, form, hideFormModal, onSubmit, schoolId, showError],
  );

  return (
    <>
      <FormProvider {...form}>
        <form>
          <Stack>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box
                sx={{
                  '& :hover': {
                    '.userInfo': {
                      visibility: 'visible',
                    },
                  },
                }}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="h2">{getUserFullNameWithTitle(parent)}</Typography>
                  <Stack>
                    <IconButton
                      className="userInfo"
                      inverse
                      sx={{
                        visibility: 'hidden',
                        '&.MuiButtonBase-root': {
                          cursor: canEditPersonalData ? 'pointer' : 'default',
                        },
                      }}
                      onClick={() => canEditPersonalData && onEditClick('userInfo')}
                    >
                      {canEditPersonalData ? <EditIcon /> : readonlyIcon}
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
              {Number.isInteger(parent.relationship_type) && (
                <Stack
                  sx={{
                    direction: 'row',
                    '& :hover': {
                      '.relationshipType': {
                        visibility: 'visible',
                      },
                    },
                  }}
                >
                  <Box>
                    <Stack direction="row" gap={0.75}>
                      <Bubble
                        disableHover
                        label={
                          <FormattedMessage
                            id={`relationship-Type-${RelationsToChild[parent.relationship_type!]}`}
                          />
                        }
                      />

                      <IconButton
                        className="relationshipType"
                        inverse
                        sx={{ visibility: 'hidden' }}
                        onClick={() => canEditPersonalData && onEditClick('relationshipType')}
                      >
                        {canEditPersonalData ? <EditIcon /> : readonlyIcon}
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Stack>

            <Stack direction="row" gap={2}>
              <Stack
                flex="1 1 50%"
                direction={!parentHasCustomFields ? 'row' : 'column'}
                spacing={3}
                pt={2}
              >
                <RowsContainer
                  stretch={!parentHasCustomFields}
                  containerTitle="applications-PersonalInformation"
                >
                  {Number.isInteger(parent.gender) && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('gender')}
                      editable={canEditPersonalData}
                      editIconName="gender"
                      updating={updating?.['gender']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Icon>{parent.gender === Genders.Male ? <MaleIcon /> : <FemaleIcon />}</Icon>
                      <GridRowName>
                        {formatMessage({
                          id: getLabelIdForOption({
                            key: Genders[parent.gender!],
                            optionsKey: 'gender',
                          }),
                        })}
                      </GridRowName>
                    </ApplicationPreviewInfoRow>
                  )}
                  {!!parent.nationalities?.length && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('nationalities')}
                      editable={canEditPersonalData}
                      editIconName="nationalities"
                      updating={updating?.['nationalities']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Icon>
                        <FlagIcon />
                      </Icon>
                      <GridRowName>{nationalitiesString}</GridRowName>
                    </ApplicationPreviewInfoRow>
                  )}
                  {Number.isInteger(parent.language) && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('language')}
                      editable={canEditPersonalData}
                      editIconName="language"
                      updating={updating?.['language']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Icon>
                        <Language3Icon className="reset-svg-currentColor" />
                      </Icon>

                      <Stack direction="row" alignItems="center" flexGrow={1}>
                        <GridRowName>
                          {formatMessage({
                            id: getLabelIdForOption({
                              key: Languages[parent.language!],
                              optionsKey: 'language',
                            }),
                          })}
                        </GridRowName>
                      </Stack>
                    </ApplicationPreviewInfoRow>
                  )}
                  {parent.profession && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('profession')}
                      editable={canEditPersonalData}
                      editIconName="profession"
                      updating={updating?.['profession']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Stack direction="row" alignItems="center" gap={1} maxWidth={400}>
                        <Icon>
                          <ProfessionIcon />
                        </Icon>
                        <GridRowName
                          maxWidth="90%"
                          sx={(theme) => ({
                            '& .professionTooltip': {
                              '.UIKit-LongNameWithVerticalTooltip__info': {
                                paddingX: 0,
                              },
                              '&.UIKit-LongNameWithVerticalTooltip_tooltip': {
                                ':hover': {
                                  '.UIKit-LongNameWithVerticalTooltip__info': {
                                    backgroundColor: theme.palette.common.white,
                                    paddingX: theme.spacing(1),
                                  },
                                },
                              },
                            },
                          })}
                        >
                          <LongNameWithVerticalTooltip className="professionTooltip">
                            {parent.profession}
                          </LongNameWithVerticalTooltip>
                        </GridRowName>
                      </Stack>
                    </ApplicationPreviewInfoRow>
                  )}
                </RowsContainer>

                <RowsContainer
                  stretch={!parentHasCustomFields}
                  containerTitle="applications-ContactInformation"
                >
                  {parent.email && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('email')}
                      editable={canEditPersonalData}
                      editIconName="email"
                      updating={updating?.['email']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Stack direction="row" alignItems="center" gap={1} maxWidth={400}>
                        <Icon>
                          <EmailIcon />
                        </Icon>
                        <GridRowName
                          maxWidth="100%"
                          sx={(theme) => ({
                            '& .emailTooltip': {
                              '.UIKit-LongNameWithVerticalTooltip__info': {
                                paddingX: 0,
                              },
                              '&.UIKit-LongNameWithVerticalTooltip_tooltip': {
                                ':hover': {
                                  '.UIKit-LongNameWithVerticalTooltip__info': {
                                    backgroundColor: theme.palette.common.white,
                                    paddingX: theme.spacing(1),
                                  },
                                },
                              },
                            },
                          })}
                        >
                          <LongNameWithVerticalTooltip className="emailTooltip">
                            <Link
                              sx={{
                                transition: 'none',
                              }}
                              underline="hover"
                              href={`mailto:${parent.email}`}
                              target="_blank"
                            >
                              {parent.email}
                            </Link>
                          </LongNameWithVerticalTooltip>
                        </GridRowName>
                      </Stack>
                    </ApplicationPreviewInfoRow>
                  )}
                  {(parent.telephone || parent.phoneCode) && (
                    <ApplicationPreviewInfoRow
                      onEdit={() => canEditPersonalData && onEditClick('telephone')}
                      editable={canEditPersonalData}
                      editIconName="telephone"
                      updating={updating?.['telephone']}
                      updatingIcon={updatingIcon}
                      readonlyIcon={readonlyIcon}
                      withoutActionBackground={withoutActionBackground}
                    >
                      <Icon>
                        <PhoneIcon />
                      </Icon>
                      <GridRowName>{`${parent.phoneCode ?? ''}${' '}${
                        parent.telephone
                      }`}</GridRowName>
                    </ApplicationPreviewInfoRow>
                  )}
                </RowsContainer>
              </Stack>

              {parentHasCustomFields && (
                <Stack flex="1 1 50%" spacing={3} pt={2}>
                  <ApplicationAdditionalInformationPreview
                    customFields={customFieldsToShow}
                    editable={canEditCustomFields}
                    updating={updating}
                    updatingIcon={updatingIcon}
                    readonlyIcon={readonlyIcon}
                    withoutActionBackground={withoutActionBackground}
                    onEdit={(customField) =>
                      canEditCustomFields && onEditClick('customFields', customField)
                    }
                  />
                </Stack>
              )}
            </Stack>

            <EditDialog
              name={getUserFullNameWithTitle(parent)}
              isOpen={isFormOpen}
              key={`edit-${parent.id}`}
              onClose={() => {
                form.reset(parent);
                hideFormModal();
              }}
              onConfirm={async () => {
                const isValid = await form.trigger();

                if (!isValid) {
                  return;
                }

                handleSubmit(form.getValues(), previewType ?? '');
              }}
              loading={isValidating}
            >
              {previewType === 'customFields' && selectedCustomField ? (
                <CustomFieldFormContainer selectedCustomField={selectedCustomField} />
              ) : (
                formContent
              )}
            </EditDialog>
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};
